import {
  createPrintContext,
  relativeToPlacementMm,
  type PlacementMm,
  type PrintContext,
} from '@designdeck/shared';
import { buildPrintContextParamsForPosition } from '@/lib/printContextBuilder';
import i18n from '@/lib/i18n';
import {
  CUSTOM_PLACEMENT,
  resolveGuidedPlacement,
  useGuidedEntryStore,
  type GuidedSideState,
} from '@/stores/guidedEntryStore';
import type { Product } from '@/api/products';

/**
 * Turning guided-entry intent into writable position state.
 *
 * Kept free of React and of any store so the decisions — which position, which
 * print area, what happens when a side cannot be committed — are testable on
 * their own.
 */

/**
 * Print context of one position on the reference product.
 *
 * The single source for both sizing a card and writing canvas state — using
 * the store's `activePrintContext` for one and this for the other would size a
 * back motif against the front print area.
 */
export function printContextForPosition(
  product: Product | undefined,
  position: string
): PrintContext | null {
  // Only a calibrated template gives the area its rectangle (R13).
  const params = buildPrintContextParamsForPosition(product, position);
  return params ? createPrintContext(params) : null;
}

/** One position ready for canvasStateManager.writePositionFromPlacement. */
export interface GuidedCommitEntry {
  position: string;
  productPositionId: string;
  printContext: PrintContext;
  designCropUrl: string;
  /**
   * Natural size of the motif behind designCropUrl. Fabric cuts its source
   * rectangle from these numbers, so a wrong value paints only part of the
   * object rather than merely mis-sizing it.
   */
  source: { width: number; height: number };
  /** Preset the seller chose; persisted so the design re-opens the same way. */
  placementPresetId: string;
  /** Design width in mm on the reference Print Area (placementMm.width). */
  designWidthMm: number;
  /**
   * Fabric custom properties for the synthesized object. An embroidery motif
   * carries its `embFileKey` and `confirmedMapping` here, so the publish rules
   * and the persisted thread mapping read it like an editor-made object.
   */
  objectProps?: Record<string, unknown>;
  /** The design box in mm on the reference Print Area (R17). */
  placementMm: PlacementMm;
}

/** Why a side with a motif still could not be committed. */
export type GuidedCommitSkip =
  /** The reference product has no position with that slug. */
  | 'no-position'
  /** The position carries no print area, so nothing can be normalized. */
  | 'no-print-area'
  /** Custom placement selected but never drawn, or an unknown preset id. */
  | 'no-placement';

export interface GuidedCommitPlan {
  entries: GuidedCommitEntry[];
  /** Sides that hold a motif but produced no entry, with the reason. */
  skipped: { position: string; reason: GuidedCommitSkip }[];
}

/**
 * Build the write plan for the sides the seller filled.
 *
 * A side without a motif is not a problem and is simply absent. A side WITH a
 * motif that cannot be committed is reported in `skipped` rather than dropped
 * silently — that is the failure mode the save path already has, and repeating
 * it here would lose the seller's motif without a word.
 */
export function planGuidedCommit(
  sides: { position: string; state: GuidedSideState }[],
  referenceProduct: Product | undefined
): GuidedCommitPlan {
  const entries: GuidedCommitEntry[] = [];
  const skipped: { position: string; reason: GuidedCommitSkip }[] = [];

  for (const { position, state } of sides) {
    if (!state.motif) continue;

    // A custom placement belongs to the canvas: its JSON carries the rotation
    // and exact transform, and re-synthesizing from a RelativeDesignState would
    // silently flatten both.
    if (state.placement === CUSTOM_PLACEMENT) continue;

    const productPosition = referenceProduct?.positions?.find(
      (p) => p.slug === position
    );
    if (!productPosition) {
      skipped.push({ position, reason: 'no-position' });
      continue;
    }
    const printContext = printContextForPosition(referenceProduct, position);
    if (!printContext) {
      skipped.push({ position, reason: 'no-print-area' });
      continue;
    }

    const relative = resolveGuidedPlacement(state, {
      widthPx: printContext.widthPx,
      heightPx: printContext.heightPx,
      pxPerMm: printContext.pxPerMm,
    });
    if (!relative) {
      skipped.push({ position, reason: 'no-placement' });
      continue;
    }
    const placementMm = relativeToPlacementMm(relative, {
      widthMm: printContext.widthMm,
      heightMm: printContext.heightMm,
    });

    const embroidery = state.embroidery;
    entries.push({
      position,
      productPositionId: productPosition.id,
      printContext,
      designCropUrl: state.motif.url,
      source: { width: state.motif.width, height: state.motif.height },
      placementPresetId: state.placement,
      designWidthMm: Math.round(placementMm.width),
      placementMm,
      ...(embroidery?.mapping && {
        objectProps: {
          name: `embroidery_${embroidery.fileName}`,
          embFileKey: embroidery.embFileKey,
          confirmedMapping: { ...embroidery.mapping },
        },
      }),
    });
  }

  return { entries, skipped };
}

/** Explanation for a side that could not be committed, in the seller's language. */
export function guidedCommitSkipMessage(
  position: string,
  reason: GuidedCommitSkip
): string {
  const side = i18n.t(
    position === 'back'
      ? 'creator.guided.backTitle'
      : useGuidedEntryStore.getState().procedure === 'embroidery'
        ? 'creator.guided.embroideryTitle'
        : 'creator.guided.frontTitle'
  );
  switch (reason) {
    case 'no-position':
      return i18n.t('creator.guided.skip.noPosition', { side });
    case 'no-print-area':
      return i18n.t('creator.guided.skip.noPrintArea', { side });
    case 'no-placement':
      return i18n.t('creator.guided.skip.noPlacement', { side });
  }
}
